import React, { useState } from "react";
import "../../Assets/Styles/theme-bridge.css";
import Modal from "react-bootstrap/Modal";
import { OnbShell, OnbStep, OnbOptions, OnbCountryPicker } from "../Onboarding/OnbKit";
import { debounce } from "debounce";
import { searchCountries } from "../../Services/Function";
import { getKnownProfile } from "../../Services/studentAuth";
import { visibleSteps, firstStep, nextStep, prevStep, stepProgress } from "../../utils/onboardingSteps";

/**
 * FILL-ONCE step map. Steps 1 and 2 (test type, math language) describe THIS
 * attempt and are always asked; the rest are durable profile answers.
 */
const STEPS = [
  { page: "1" }, // math vs english — per-attempt
  { page: "2" }, // math language — per-attempt
  { page: "3", fields: ["destination"] },
  { page: "4", fields: ["nationality"] },
  { page: "5", fields: ["degree"] },
];

const OnboardingModal = ({ language, onComplete, onClose }) => {
  const [known] = useState(getKnownProfile);
  const [page, setPage] = useState(() => firstStep(STEPS, known) || "1");
  // direction of the last step change, drives the enter animation
  const [navDir, setNavDir] = useState("fwd");
  const [testType, setTestType] = useState({});
  const [testLanguage, setTestLanguage] = useState({});
  const [destination, setDestination] = useState({});
  const [selectedCountry, setSelectedCountry] = useState({});
  const [degree, setDegree] = useState({});
  const [majors, setMajors] = useState("");
  const [cgpa, setCgpa] = useState("");
  const [previousSchool, setPreviousSchool] = useState("");
  const [accomplished, setAccomplished] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [countries, setcountries] = useState([]);
  const [loading, setloading] = useState(false);
  const [searchText, setsearchText] = useState("");

  const recommendedCountries = [
    {
      english_name: "Kuwait",
      arabic_name: "الكويت",
      alpha2_code: "KW",
      alpha3_code: "KWT",
      phone_code: "965",
      id: "63cfb299df954c47a6fde217",
    },
    {
      english_name: "Saudi Arabia",
      arabic_name: "السعودية",
      alpha2_code: "SA",
      alpha3_code: "SAU",
      phone_code: "966",
      id: "63cfb299df954c47a6fde264",
    },
    {
      english_name: "Qatar",
      arabic_name: "قطر",
      alpha2_code: "QA",
      alpha3_code: "QAT",
      phone_code: "974",
      id: "63cfb299df954c47a6fde255",
    },
    {
      english_name: "Oman",
      arabic_name: "سلطنة عمان",
      alpha2_code: "OM",
      alpha3_code: "OMN",
      phone_code: "968",
      id: "63cfb299df954c47a6fde248",
    },
    {
      english_name: "United Arab Emirates",
      arabic_name: "الإمارات العربية المتحدة",
      alpha2_code: "AE",
      alpha3_code: "ARE",
      phone_code: "971",
      id: "63cfb299df954c47a6fde28d",
    },
  ];

  const arabic = language === "ar";

  const nextPage = (next) => {
    setNavDir(parseInt(next) < parseInt(page) ? "back" : "fwd");
    setPage(next);
  };

  // Progress = position in the ACTUAL path (the English test skips the
  // math-language step), reaching 100% only on the last step. The old
  // `page * 15` maxed out at 75% and ignored the skipped step.
  // The English test skips the math-language step; fill-once may skip the
  // profile steps. Progress runs over what is ACTUALLY shown.
  const pathSteps = STEPS.filter(
    (step) => !(testType.en_name === "English" && step.page === "2"),
  );
  const progress = stepProgress(pathSteps, known, page);

  /**
   * Advance past `from` through the visible flow; finish when none are left.
   * `overrides` carries anything chosen in this same tick, because finishing
   * here reads values React has not flushed yet.
   */
  const advance = (from, overrides = {}) => {
    const next = nextStep(pathSteps, known, from);
    if (next) nextPage(next);
    else completeOnboarding(overrides);
  };

  const onChangeTestType = (data) => {
    setAccomplished((prev) => [...prev, "pageone"]);
    setTestType(data);
    if (data.en_name === "Math") {
      nextPage("2"); // Go to language selection for math
    } else {
      // For English test, set default language to English and skip language selection
      const englishLanguage = { ar_name: "الإنجليزية", en_name: "English" };
      setTestLanguage(englishLanguage);
      // fill-once may also skip step 3+, so route through the visible flow
      const next = nextStep(
        STEPS.filter((step) => step.page !== "2"),
        known,
        "1",
      );
      if (next) nextPage(next);
      else completeOnboarding({ testType: data, testLanguage: englishLanguage });
    }
  };

  const onChangeTestLanguage = (data) => {
    setAccomplished((prev) => [...prev, "pagetwo"]);
    setTestLanguage(data);
    advance("2", { testLanguage: data });
  };

  const onChangeDestination = (data) => {
    setAccomplished((prev) => [...prev, "pagethree"]);
    setDestination(data);
    advance("3", { destination: data });
  };

  const onChangeDegree = (data) => {
    setDegree(data);
  };

  const resetValues = () => {
    setAccomplished([]);
    setTestType({});
    setTestLanguage({});
    setDestination({});
    setSelectedCountry({});
    setDegree({});
    setMajors("");
    setCgpa("");
    setPreviousSchool("");
    setErrorMessage("");
  };

  /**
   * @param {Object} [overrides] values chosen in the SAME tick as this call.
   *
   * Every step handler both sets state and may finish the flow immediately —
   * for a signed-in student whose profile steps are all skipped, choosing the
   * test language is the last action, so `advance` completes right away. React
   * has not flushed that setState yet, so reading `testLanguage` from state
   * here yields the previous value: `{}`.
   *
   * That is exactly how "Math + Arabic" served English questions. `{}` is
   * truthy, so AptitudeTest.js's `testLanguage?.en_name?.toLowerCase() || … || "ar"`
   * fallback passed the empty object straight through, and QuizStep read
   * `{}.en_name` as undefined and fell to its English branch.
   *
   * `degree` was already worked around by passing it positionally; this
   * generalises that to every value the flow can finish on.
   */
  const completeOnboarding = (overrides = {}) => {
    // FILL-ONCE: a skipped step leaves its state as an EMPTY OBJECT (which is
    // truthy), so fall back on the shape the consumer reads, not on truthiness.
    // AptitudeTest.js reads destination.en_name / selectedCountry.english_name
    // / degree.en_name, so a string from the profile is re-wrapped to match.
    const asOption = (value) =>
      typeof value === "string" ? { en_name: value, ar_name: value } : value;
    const asCountry = (value) =>
      typeof value === "string"
        ? { english_name: value, arabic_name: value }
        : value;

    // a same-tick choice wins over state, which may not have flushed yet
    const testTypeState = overrides.testType || testType;
    const testLanguageState = overrides.testLanguage || testLanguage;
    const degreeState = overrides.degree || degree;
    const destinationState = overrides.destination || destination;
    const countryState = overrides.selectedCountry || selectedCountry;

    const destinationValue = destinationState?.en_name
      ? destinationState
      : asOption(known.destination) || destinationState;
    const countryValue = countryState?.english_name
      ? countryState
      : asCountry(known.nationality) || countryState;
    const degreeValue = degreeState?.en_name
      ? degreeState
      : asOption(known.degree) || degreeState;

    const onboardingData = {
      testType: testTypeState,
      testLanguage: testLanguageState,
      destination: destinationValue,
      selectedCountry: countryValue,
      degree: degreeValue,
      majors,
      cgpa,
      previousSchool,
     
    };
    
    console.log("onboardingData", JSON.stringify(onboardingData, null, 2));
    onComplete(onboardingData);
  };

  const searchCountry = debounce(async (e) => {
    if (!e.target.value) {
      setcountries([]);
    } else {
      setloading(true);
      const res = await searchCountries({
        text: e.target.value,
        sortBy: "createdAt:desc",
        limit: 7,
      });
      setcountries(res?.results);
      setloading(false);
    }
  }, 500);

  /* ---------------------------------------------------------------------
     RENDER — rebuilt from scratch on the new theme. All state, handlers and
     completion semantics above are untouched; only the presentation changed.
     Steps show as: icon chip → question → staggered option cards, with a
     direction-aware slide between steps and a live "step x of y" progress.
     --------------------------------------------------------------------- */
  const path = visibleSteps(pathSteps, known);
  const stepIdx = Math.max(0, path.findIndex((s) => s.page === page)) + 1;
  const stepTotal = path.length;

  const STEP_META = {
    1: { icon: "📝", title: arabic ? "أي اختبار تود أن تأخد؟" : "Which test do you want to take?", hint: arabic ? "اختبار مجاني وسريع — النتيجة فور الانتهاء" : "Free and quick — your result the moment you finish" },
    2: { icon: "🌐", title: arabic ? "بأي لغة تريد أن تأخذ اختبار الرياضيات؟" : "In which language do you want the maths test?", hint: arabic ? "اختر اللغة الأنسب لك — الأسئلة نفسها في الحالتين" : "Pick whichever suits you — the questions are the same" },
    3: { icon: "✈️", title: arabic ? "في أي بلد ترغب بالدراسة؟" : "Where do you want to study?", hint: "" },
    4: { icon: "🌍", title: arabic ? "ما هي جنسيتك؟" : "What is your nationality?", hint: "" },
    5: { icon: "🎓", title: arabic ? "ماذا تريد أن تدرس؟" : "What do you want to study?", hint: "" },
  };
  const meta = STEP_META[page] || STEP_META["1"];

  const OPTION_SETS = {
    1: {
      selected: testType.en_name,
      options: [
        { key: "Math", icon: "🔢", label: arabic ? "الرياضيات" : "Maths", onPick: () => onChangeTestType({ ar_name: "الرياضيات", en_name: "Math" }) },
        { key: "English", icon: "📚", label: arabic ? "الإنجليزية" : "English", onPick: () => onChangeTestType({ ar_name: "الإنجليزية", en_name: "English" }) },
      ],
    },
    2: {
      selected: testLanguage.en_name,
      options: [
        { key: "Arabic", icon: "🇰🇼", label: arabic ? "العربية" : "Arabic", onPick: () => onChangeTestLanguage({ ar_name: "العربية", en_name: "Arabic" }) },
        { key: "English", icon: "🇬🇧", label: "English", onPick: () => onChangeTestLanguage({ ar_name: "الإنجليزية", en_name: "English" }) },
      ],
    },
    3: {
      selected: destination.en_name,
      options: [
        { key: "UK", icon: "🇬🇧", label: arabic ? "بريطانيا" : "United Kingdom", onPick: () => onChangeDestination({ ar_name: "بريطانيا", en_name: "UK" }) },
        { key: "US", icon: "🇺🇸", label: arabic ? "أمريكا" : "United States", onPick: () => onChangeDestination({ ar_name: "أمريكا", en_name: "US" }) },
        { key: "Australia", icon: "🇦🇺", label: arabic ? "أستراليا" : "Australia", onPick: () => onChangeDestination({ ar_name: "أستراليا", en_name: "Australia" }) },
        { key: "Others", icon: "🌏", label: arabic ? "غيرها" : "Somewhere else", onPick: () => onChangeDestination({ ar_name: "غيرها", en_name: "Others" }) },
      ],
    },
    5: {
      selected: degree.en_name,
      options: [
        { key: "Bachelor's", icon: "🎒", label: arabic ? "البكلوريوس" : "Bachelor's", onPick: () => { onChangeDegree({ en_name: "Bachelor's", ar_name: "البكلوريوس" }); setAccomplished((prev) => [...prev, "pagefive"]); completeOnboarding({ degree: { en_name: "Bachelor's", ar_name: "البكلوريوس" } }); } },
        { key: "Master's", icon: "🧑‍🎓", label: arabic ? "الماجستير" : "Master's", onPick: () => { onChangeDegree({ en_name: "Master's", ar_name: "الماجستير" }); setAccomplished((prev) => [...prev, "pagefive"]); completeOnboarding({ degree: { en_name: "Master's", ar_name: "الماجستير" } }); } },
        { key: "PhD", icon: "🔬", label: arabic ? "الدكتوراة" : "PhD", onPick: () => { onChangeDegree({ en_name: "PhD", ar_name: "الدكتوراة" }); setAccomplished((prev) => [...prev, "pagefive"]); completeOnboarding({ degree: { en_name: "PhD", ar_name: "الدكتوراة" } }); } },
      ],
    },
  };

  const optionSet = OPTION_SETS[page];

  const pickNationality = (country) => {
    setSelectedCountry(country);
    setErrorMessage("");
    accomplished.push("pagefour");
    advance("4", { selectedCountry: country });
    setcountries([]);
    setsearchText("");
  };

  return (
    <Modal
      show={true}
      fullscreen={true}
      contentClassName="onb2-content"
      aria-labelledby="onb2-question"
      onHide={() => { resetValues(); onClose(); }}
    >
      <OnbShell
        arabic={arabic}
        stepIdx={stepIdx}
        stepTotal={stepTotal}
        progress={progress}
        showBack={!!prevStep(STEPS, known, page)}
        onBack={() => nextPage(prevStep(STEPS, known, page))}
        onClose={() => { resetValues(); onClose(); }}
      >
        <OnbStep key={page} navDir={navDir} icon={meta.icon} title={meta.title} hint={meta.hint}>
          {optionSet ? (
            <OnbOptions options={optionSet.options} selectedKey={optionSet.selected} />
          ) : (
            <>
              <OnbCountryPicker
                arabic={arabic}
                searchText={searchText}
                onSearchInput={(e) => { setsearchText(e.target.value); searchCountry(e); }}
                countries={countries}
                loading={loading}
                recommended={recommendedCountries.slice(0, 5)}
                selectedId={selectedCountry?.id}
                onPick={pickNationality}
              />
              {errorMessage ? <div className="onb2-err">{errorMessage}</div> : null}
            </>
          )}
        </OnbStep>
      </OnbShell>
    </Modal>
  );
};

export default OnboardingModal;
